import { Icon, Popup } from "semantic-ui-react";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import money from "astrid-helpers/src/money";

import usePriceTypes from "../../../../prices/hooks/usePriceTypes";
import { priceAccessor } from "../../../../prices/utils/priceAccessors";

import FinalizedCostActions from "../components/FinalizedCostActions";

export default function useFinalizedCostsTableColumns({ partyId }) {
	const { t } = useTranslation();

	const priceTypeNames = usePriceTypes();

	return useMemo(
		() => [
			{
				id: "secondParty.name",
				filter: "text",
				Header: t("secondParty", "Second party"),
			},
			{
				id: "price.type",
				filter: "select",
				Header: t("type", "Type"),
				accessor: ({ price }) => priceTypeNames[price.type],
			},
			{
				id: "price.price",
				filter: "text",
				Header: t("price", "Price"),
				accessor: (cost) =>
					cost?.price?.price === null ? t("hiddenPrice", "Hidden price") : priceAccessor(cost.price),
			},
			{
				id: "created",
				Header: t("created", "Created"),
				accessor: ({ created }) => {
					return created?.toDate().toLocaleString();
				},
			},
			{
				id: "orderId",
				Header: t("order", "Order"),
				Cell: ({ row, value }) => {
					if (row.original.firstParty.type !== organizationTypes.PRODUCER) {
						return value;
					}
					return (
						<Link
							to={`/${row.original.firstParty.type}/${row.original.firstParty.id}/finance/purchases/${row.original.orderId}`}
						>
							{value}
						</Link>
					);
				},
			},
			{
				id: "total",
				Header: t("sum", "Sum"),
				textAlign: "right",
				accessor: ({ total, expense, secondParty }) => {
					return total * (expense ? -1 : 1) * (partyId === secondParty?.id ? -1 : 1);
				},
				Cell: ({ row, value }) => (
					<>
						{money(value, row.original.price.currency)}

						{row.original.employee && (
							<Popup
								trigger={<Icon name="stopwatch" />}
								inverted
								size="mini"
								content={
									row.original.amountProduced
										? t("producedTime", {
												mins: Math.floor(row.original.amountProduced),
										  })
										: t("costAgreementEmployed")
								}
							/>
						)}
					</>
				),
			},
			{
				id: "actions",
				textAlign: "right",
				collapsing: true,
				Cell: FinalizedCostActions,
			},
		],
		[priceTypeNames, partyId, t],
	);
}
